import React, { useEffect, useState } from "react";
import { Box, Card, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import RecentDonations from "../../../stream-management/components/memorial-page/components/chema/RecentDonations";
import DonationsTable from "../../../stream-management/components/memorial-page/components/chema/DonationsTable";
import type { Donation } from "../../../stream-management/components/memorial-page/components/chema/types";
import { STORAGE_KEY } from "../../../stream-management/components/memorial-page/components/chema/types";
import apiService from "../../../../service/Api";

interface StreamResponse {
  id: number;
  title: string;
  date: string;
  booking: {
    id: number;
    title: string;
  };
  go_fund_me_enabled: boolean;
}

interface StreamsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: StreamResponse[];
}

const ChemaDashboard: React.FC = () => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [bookingIds, setBookingIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    type StoredDonation = Omit<Donation, "date"> & {
      date: string;
    };

    try {
      const parsed = (JSON.parse(stored) as StoredDonation[]).map(
        (d): Donation => ({
          ...d,
          date: new Date(d.date),
        })
      );

      setDonations(parsed);
    } catch (e) {
      console.error("Failed to parse donations from storage:", e);
    }
  }, []);

  useEffect(() => {
    const fetchStreams = async () => {
      setLoading(true);
      try {
        // Fetch all streams with go_fund_me_enabled
        const response = await apiService.get<StreamsApiResponse>(
          "/api/streams/stream/",
          { go_fund_me_enabled: true }
        );

        const streams = response.data?.results || [];
        // Extract unique booking IDs
        const ids = streams
          .map((stream) => stream.booking?.id)
          .filter((id): id is number => id !== undefined && id !== null);
        
        setBookingIds([...new Set(ids)]); // Remove duplicates
      } catch (error) {
        console.error("Error fetching streams:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();
  }, []);


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card sx={{ p: 2 }}>
            <RecentDonations donations={donations} />
          </Card>
        </Grid>
        {bookingIds.length > 0 ? (
          bookingIds.map((bookingId) => (
            <Grid size={{ xs: 12 }} key={bookingId}>
              <DonationsTable
                bookingId={bookingId}
                title="All Donations"
                ordering="-created_at"
              />
            </Grid>
          ))
        ) : (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ p: 2 }}>
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No streams with CHEMA enabled found
                </Typography>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ChemaDashboard;
